import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Sun, Moon, Sparkles, Trash2, Loader2, LogOut, MapPin, X, Heart } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StarField } from "../components/StarField";

const EMPTY = "https://images.pexels.com/photos/7222056/pexels-photo-7222056.jpeg";

function CreateChartModal({ onClose, onCreated, charts, navigate }) {
  const [mode, setMode] = useState("profile");
  const [form, setForm] = useState({ name: "", birth_date: "", birth_time: "12:00", gender: "female" });
  const [cityQuery, setCityQuery] = useState("");
  const [cityResults, setCityResults] = useState([]);
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [synA, setSynA] = useState("");
  const [synB, setSynB] = useState("");
  const timer = useRef(null);

  const onCityChange = (v) => {
    setCityQuery(v);
    setCity(null);
    clearTimeout(timer.current);
    if (v.length < 2) return setCityResults([]);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/cities/search?q=${encodeURIComponent(v)}`);
        setCityResults(data.results);
      } catch { /* ignore */ }
    }, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!city) return toast.error("Please pick your birth city from the list");
    setLoading(true);
    try {
      const { data } = await api.post("/charts", {
        name: form.name, birth_date: form.birth_date, birth_time: form.birth_time,
        birth_place: city.label, lat: city.lat, lon: city.lon, timezone: city.timezone,
        gender: form.gender,
      });
      toast.success("Birth chart cast successfully");
      onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const startSynastry = () => {
    if (!synA || !synB) return toast.error("Pick two profiles");
    if (synA === synB) return toast.error("Choose two different people");
    onClose();
    navigate(`/synastry?a=${synA}&b=${synB}`);
  };

  const selectCls = "w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#45F0DF]/50 [color-scheme:dark]";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="create-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="glass relative w-full max-w-lg rounded-2xl p-8">
        <button onClick={onClose} className="absolute right-5 top-5 text-white/50 hover:text-white" data-testid="close-modal"><X className="h-5 w-5" /></button>

        <div className="flex rounded-full border border-white/10 p-1 mb-6 max-w-xs">
          <button onClick={() => setMode("profile")} data-testid="mode-profile" className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors duration-300 ${mode === "profile" ? "bg-white/10 text-white" : "text-white/50"}`}>New Profile</button>
          <button onClick={() => setMode("synastry")} data-testid="mode-synastry" className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors duration-300 ${mode === "synastry" ? "bg-white/10 text-white" : "text-white/50"}`}>Synastry</button>
        </div>

        {mode === "profile" ? (
          <>
            <h2 className="text-3xl font-serif mb-6">Cast a Birth Chart</h2>
            <form onSubmit={submit} className="space-y-4">
              <input data-testid="chart-name-input" required placeholder="Whose chart is this?" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#45F0DF]/50 transition-colors duration-300" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline text-white/40 block mb-2">Birth Date</label>
                  <input data-testid="birth-date-input" required type="date" value={form.birth_date}
                    onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-3 text-sm outline-none focus:border-[#45F0DF]/50 [color-scheme:dark]" />
                </div>
                <div>
                  <label className="overline text-white/40 block mb-2">Birth Time</label>
                  <input data-testid="birth-time-input" required type="time" value={form.birth_time}
                    onChange={(e) => setForm({ ...form, birth_time: e.target.value })}
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-3 text-sm outline-none focus:border-[#45F0DF]/50 [color-scheme:dark]" />
                </div>
              </div>
              <div>
                <label className="overline text-white/40 block mb-2">Gender (for BaZi luck pillars)</label>
                <select data-testid="gender-select" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className={selectCls}>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                </select>
              </div>
              <div className="relative">
                <label className="overline text-white/40 block mb-2">Birth City</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <input data-testid="city-input" placeholder="Search city..." value={city ? city.label : cityQuery}
                    onChange={(e) => onCityChange(e.target.value)}
                    className="w-full rounded-lg bg-white/5 border border-white/10 pl-9 pr-4 py-3 text-sm outline-none focus:border-[#45F0DF]/50 transition-colors duration-300" />
                </div>
                {cityResults.length > 0 && !city && (
                  <div className="absolute z-10 mt-2 w-full max-h-52 overflow-auto rounded-lg glass border border-white/10" data-testid="city-results">
                    {cityResults.map((c, i) => (
                      <button key={i} type="button" onClick={() => { setCity(c); setCityResults([]); }}
                        className="block w-full text-left px-4 py-2.5 text-sm hover:bg-white/10 transition-colors" data-testid={`city-option-${i}`}>{c.label}</button>
                    ))}
                  </div>
                )}
              </div>
              <button data-testid="submit-chart" type="submit" disabled={loading}
                className="btn-gold w-full rounded-lg py-3 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Reveal the Chart
              </button>
            </form>
          </>
        ) : (
          <>
            <h2 className="text-3xl font-serif mb-2">Synastry Reading</h2>
            <p className="text-sm text-white/50 mb-6">Choose two profiles to weave their charts together.</p>
            {charts.length < 2 ? (
              <p className="text-white/60 text-sm">Create at least two profiles first.</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="overline text-white/40 block mb-2">Person A</label>
                  <select data-testid="synastry-select-a" value={synA} onChange={(e) => setSynA(e.target.value)} className={selectCls}>
                    <option value="">Select…</option>
                    {charts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline text-white/40 block mb-2">Person B</label>
                  <select data-testid="synastry-select-b" value={synB} onChange={(e) => setSynB(e.target.value)} className={selectCls}>
                    <option value="">Select…</option>
                    {charts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <button data-testid="synastry-start" onClick={startSynastry} className="btn-gold w-full rounded-lg py-3 text-sm font-semibold flex items-center justify-center gap-2">
                  <Heart className="h-4 w-4" /> Build Synastry
                </button>
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [charts, setCharts] = useState(null);
  const [synastries, setSynastries] = useState([]);
  const [modal, setModal] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    api.get("/charts").then((r) => setCharts(r.data.charts));
    api.get("/synastry").then((r) => setSynastries(r.data.synastries || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const remove = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/charts/${id}`);
    toast.success("Chart removed");
    load();
  };

  const removeSyn = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/synastry/${id}`);
    toast.success("Synastry removed");
    load();
  };

  return (
    <div className="min-h-screen cosmic-bg grain relative">
      <StarField count={60} />
      <header className="glass sticky top-0 z-40 border-b border-white/10">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Moon className="h-5 w-5 gold-text" strokeWidth={1.5} />
            <span className="text-2xl font-serif tracking-tight">Astro Trinity</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/50 hidden sm:block">Welcome, {user?.name}</span>
            <button onClick={logout} data-testid="logout-btn" className="text-white/50 hover:text-white transition-colors">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-6 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <p className="overline gold-text mb-3">Your Constellations</p>
            <h1 className="text-5xl sm:text-6xl font-serif tracking-tight leading-none">Birth Charts</h1>
          </div>
          <button
            data-testid="new-chart-btn"
            onClick={() => setModal(true)}
            className="btn-gold rounded-full px-6 py-3 text-sm font-semibold flex items-center gap-2 self-start"
          >
            <Plus className="h-4 w-4" /> New Chart
          </button>
        </div>

        {charts === null ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin gold-text" /></div>
        ) : charts.length === 0 ? (
          <div className="glass rounded-2xl overflow-hidden grid md:grid-cols-2">
            <div className="p-10 flex flex-col justify-center">
              <h3 className="text-3xl font-serif mb-3">The sky awaits your story</h3>
              <p className="text-white/60 leading-relaxed mb-6">
                Cast your first birth chart to unlock readings across three ancient traditions.
              </p>
              <button onClick={() => setModal(true)} data-testid="empty-new-chart" className="btn-gold rounded-full px-6 py-3 text-sm font-semibold self-start flex items-center gap-2">
                <Plus className="h-4 w-4" /> Cast a Chart
              </button>
            </div>
            <div className="min-h-[220px]" style={{ backgroundImage: `url(${EMPTY})`, backgroundSize: "cover", backgroundPosition: "center" }} />
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {charts.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                onClick={() => navigate(`/chart/${c.id}`)}
                data-testid={`chart-card-${i}`}
                className="glass group cursor-pointer rounded-2xl p-6 hover:-translate-y-1 transition-transform duration-300"
                style={{ transitionProperty: "transform, border-color" }}
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-serif">{c.name}</h3>
                    <p className="text-xs text-white/40 mt-1">{c.birth_date} · {c.birth_place}</p>
                  </div>
                  <button onClick={(e) => remove(e, c.id)} data-testid={`delete-chart-${i}`} className="text-white/30 hover:text-red-400 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-white/5 py-3">
                    <Sun className="h-4 w-4 mx-auto mb-1 gold-text" strokeWidth={1.5} />
                    <p className="text-[10px] text-white/40 uppercase tracking-wider">Sun</p>
                    <p className="text-sm">{c.summary?.sun_sign}</p>
                  </div>
                  <div className="rounded-lg bg-white/5 py-3">
                    <Moon className="h-4 w-4 mx-auto mb-1 cyan-text" strokeWidth={1.5} />
                    <p className="text-[10px] text-white/40 uppercase tracking-wider">Moon</p>
                    <p className="text-sm">{c.summary?.moon_sign}</p>
                  </div>
                  <div className="rounded-lg bg-white/5 py-3">
                    <Sparkles className="h-4 w-4 mx-auto mb-1 text-[#8A2BE2]" strokeWidth={1.5} />
                    <p className="text-[10px] text-white/40 uppercase tracking-wider">Animal</p>
                    <p className="text-sm">{c.summary?.animal}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {synastries.length > 0 && (
          <div className="mt-16" data-testid="synastry-section">
            <div className="flex items-center gap-2 mb-6">
              <Heart className="h-5 w-5 gold-text" strokeWidth={1.5} />
              <h2 className="text-3xl font-serif tracking-tight">Synastry Readings</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {synastries.map((s, i) => (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => navigate(`/synastry?id=${s.id}`)}
                  data-testid={`synastry-card-${i}`}
                  className="glass group cursor-pointer rounded-2xl p-5 hover:-translate-y-1 transition-transform duration-300"
                  style={{ transitionProperty: "transform, border-color" }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-serif">{s.a_name}</span>
                      <Heart className="h-3.5 w-3.5 text-[#B47CF0]" />
                      <span className="text-lg font-serif">{s.b_name}</span>
                    </div>
                    <button onClick={(e) => removeSyn(e, s.id)} data-testid={`delete-synastry-${i}`} className="text-white/30 hover:text-red-400 transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-xs text-white/40 mt-2">Compatibility reading · tap to open</p>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </main>

      {modal && (
        <CreateChartModal
          charts={charts || []}
          navigate={navigate}
          onClose={() => setModal(false)}
          onCreated={(chart) => { setModal(false); navigate(`/chart/${chart.id}`); }}
        />
      )}
    </div>
  );
}
